import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, forkJoin, map, of, switchMap } from 'rxjs';
import { BadDataTicketComponent } from '../../shared/bad-data-ticket/bad-data-ticket.component';
import { BadDataService } from '../../shared/bad-data-ticket/bad-data.service';
import { Assignee, BadDataTicket } from '../../shared/bad-data-ticket/badDataTicket';
import { BadDataComponent } from '../../shared/bad-data/bad-data.component';
import { UserMenu } from '../../user';
import { Constants } from '../constants';
import { LoggerService } from '../logger.service';
import { MenuService } from '../menu.service';
import { SearchService } from '../search.service';
import { PowerBiReport, PowerBiWorkspace, UserService } from '../user.service';

import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBars, faBullhorn, faComments, faEllipsisHAlt, faEnvelope, faExclamationTriangle, faGlobe, faGripHorizontal, faHistory, faLaptopMedical, faListCheck, faQuestion, faQuestionSquare } from '@fortawesome/pro-light-svg-icons';
import moment from 'moment';
import { User } from '../../user/user';
import { MenuItemComponent } from '../menu-item/menu-item.component';


@Component({
  selector: 'cub-top-menu',
  templateUrl: './top-menu.component.html',
  styleUrls: ['./top-menu.component.scss'],
  animations: [
    trigger('showingListing', [
      state('true', style({
        transform: 'rotate(0)'
      })),
      state('false', style({
        transform: 'rotate(90deg)'
      })),
      transition('true => false', animate('500ms ease-in')),
      transition('false => true', animate('500ms ease-out'))
    ])
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatToolbarModule, MatButtonModule, FontAwesomeModule, NgIf, MatTooltipModule, FormsModule, MatMenuModule, NgFor, MatBadgeModule, MenuItemComponent]
})
export class TopMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() loggedIn: boolean;
  @Output() logoutClicked = new EventEmitter<void>();

  @ViewChild('searchBox') searchBox: ElementRef<HTMLInputElement>;
  @ViewChild('searchAnchor') searchAnchor: ElementRef<HTMLDivElement>;
  @ViewChild('toolbar', { read: ElementRef }) toolbar: ElementRef<HTMLElement>;

  // Left offset (px, relative to the toolbar) that drives the sliding search box: the measured
  // toolbar slot on normal pages, or the fixed spot above the results pane on the /search page.
  searchBoxLeft: number | null = null;
  slideReady = false;
  private readonly searchPageLeft = 445;
  private onResize = () => this.updateSearchBoxPosition();

  recents: UserMenu[] = [];
  userMenuList: UserMenu[] = [];
  logoutMenuItem: UserMenu;
  logoutMenuItemSep: UserMenu;
  user: User;
  searchTerm$ = new Subject<string>();
  showLeftMenu = true;
  leftMenuIsCollapsed = false;
  isLoading = true;
  showPointButton = false;
  powerBIWorkspaces: PowerBiWorkspace[] = [];
  powerBIReports: (PowerBiReport & { workspaceId: string })[] = [];
  reportsInWorkspace: PowerBiReport[] = [];
  powerBILoadState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  private readonly subscription = new Subscription();
  private powerBIRequest?: Subscription;
  private photoRequest?: Subscription;
  private photoReader?: FileReader;
  private photoAccountName: string | null = null;
  private photoLoaded = false;
  private photoUrl = './assets/img/noImage.png';

  faHistory = faHistory;
  faQuestionSquare = faQuestionSquare;
  faBars = faBars;
  faEllipsisHAlt = faEllipsisHAlt;
  faGripHorizontal = faGripHorizontal;
  faComments = faComments;
  faBullhorn = faBullhorn;
  faExclamationTriangle = faExclamationTriangle;
  faQuestion = faQuestion;
  faEnvelope = faEnvelope;
  faGlobe = faGlobe;
  faLaptopMedical = faLaptopMedical;
  faListCheck = faListCheck;

  screenWidth: number;
  showPoint = true;
  badDataDisabled = false;
  defaultBadDataTicket: BadDataTicket;
  badDataTicket: BadDataTicket;
  badDataCount = 0;
  myDetails: any;
  nextPasswordChange: Date;

  constructor(
    private searchService: SearchService,
    private userService: UserService,
    private menuService: MenuService,
    private loggerService: LoggerService,
    private badDataService: BadDataService,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog
  ) {
    this.screenWidth = (window.innerWidth);

    this.searchService.setSystem('ID');
    this.searchService.searchByTerm(this.searchTerm$).subscribe(results => {
      this.searchService.mapResult(results);
    });
    this.nextPasswordChange = moment().add(180, 'days').toDate(); // init so it doesn't show right away
    this.configureDefaultBadDataTicket();
    this.router.events.subscribe((v: any) => {
      // show point button if we have a small screen and the current screen is the dashboard
      if (this.screenWidth <= Constants.ResponsiveWidthTrigger) {
        if (v.url !== undefined) {
          if (v.url === '/dashboard' || v.url === '/') {
            this.showPointButton = true;
          } else {
            this.showPointButton = false;
          }
        }
      }
      // Re-target the sliding search box after each navigation (its slot depends on the route).
      if (v instanceof NavigationEnd) {
        this.updateSearchBoxPosition();
      }
    });

    this.addLogoutMenu();
  }

  // On the enterprise search page the search box is positioned above the results pane; on every
  // other route it keeps its default placement in the toolbar.
  get isSearchPage(): boolean {
    return (this.router.url || '').split('?')[0] === '/search';
  }

  ngAfterViewInit() {
    // Measure the initial slot after the view paints, then enable the sliding transition (so the
    // very first placement snaps into position instead of sliding in from the toolbar's left edge).
    setTimeout(() => {
      this.updateSearchBoxPosition();
      this.slideReady = true;
    });
    window.addEventListener('resize', this.onResize);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.cancelPhotoRequest();
    window.removeEventListener('resize', this.onResize);
  }

  // Sets searchBoxLeft to drive the box's horizontal slide: 445px above the results pane on the
  // /search page, otherwise the measured left edge of its reserved toolbar slot (.search-anchor).
  private updateSearchBoxPosition() {
    if (this.isSearchPage) {
      this.searchBoxLeft = this.searchPageLeft;
      return;
    }
    const anchor = this.searchAnchor?.nativeElement;
    const toolbar = this.toolbar?.nativeElement;
    if (anchor && toolbar) {
      this.searchBoxLeft = Math.round(
        anchor.getBoundingClientRect().left - toolbar.getBoundingClientRect().left
      );
    }
  }

  getPowerBIReports() {
    if (this.powerBILoadState === 'loading' || this.powerBILoadState === 'loaded') return;
    this.powerBILoadState = 'loading';
    this.powerBIRequest = this.userService.getPowerBIGroups().pipe(
      switchMap(response => {
        if (!Array.isArray(response?.value)) throw new Error('Power BI workspaces response was not an array');
        if (response.value.length === 0) return of<{ workspace: PowerBiWorkspace; reports: PowerBiReport[] }[]>([]);
        return forkJoin(response.value.map(workspace =>
          this.userService.getPowerBIReportsInGroup(workspace.id).pipe(map(reports => {
            if (!Array.isArray(reports?.value)) throw new Error('Power BI reports response was not an array');
            return { workspace, reports: reports.value };
          }))
        ));
      })
    ).subscribe({
      next: results => {
        this.powerBIWorkspaces = results
          .filter(result => result.reports.length > 0)
          .map(result => result.workspace)
          .sort((a, b) => a.name.localeCompare(b.name));
        this.powerBIReports = results.flatMap(result =>
          result.reports.map(report => ({ ...report, workspaceId: result.workspace.id }))
        );
        this.powerBILoadState = 'loaded';
      },
      error: error => {
        this.powerBILoadState = 'error';
        this.loggerService.logError(
          `Unable to load Power BI reports: ${error?.message ?? error}`, 'top-menu.component.ts', 'Power BI', false
        );
      }
    });
    this.subscription.add(this.powerBIRequest);
  }

  getWorkspaceReportCount(workspaceId: string): number {
    return this.powerBIReports.filter(r => r.workspaceId === workspaceId).length;
  }

  getReportsInWorkspace(workspaceId: string) {
    this.reportsInWorkspace = this.powerBIReports.filter(r => r.workspaceId === workspaceId).sort((a, b) => a.name.localeCompare(b.name));
  }

  openPowerBIReport(report: PowerBiReport) {
    this.loggerService.logPageVisit('open power bi report', report.name);
    window.open(report.webUrl);
  }

  addLogoutMenu() {
    this.logoutMenuItem = new UserMenu();
    this.logoutMenuItem.name = 'Logout';
    this.logoutMenuItem.desc = 'Log out from your account';
    this.logoutMenuItem.icon = 'faRightFromBracket';
    this.logoutMenuItem.method = 'logout';
    this.logoutMenuItem.external = false;
    this.logoutMenuItem.isCustom = true;
    this.logoutMenuItem.separator = false;
    this.logoutMenuItem.link = '';

    this.logoutMenuItemSep = new UserMenu();
    this.logoutMenuItemSep.separator = true;
  }

  ngOnInit() {
    this.getMenu();

    // Keep the search box text in sync with the active term (e.g. when the /search page
    // seeds a term from the URL on refresh) so the box isn't blank while results show.
    this.searchService.term$.subscribe(term => {
      if (this.searchBox && this.searchBox.nativeElement.value !== (term || '')) {
        this.searchBox.nativeElement.value = term || '';
      }
    });

    this.menuService.showingSystemMenu$.subscribe(result => {
      this.showLeftMenu = result;
    });
    this.menuService.minMaxLeftMenu$.subscribe(result => {
      this.leftMenuIsCollapsed = result;
    });
    this.menuService.showPoint$.subscribe(result => {
      this.showPoint = result;
    });
    this.menuService.badDataPayload$.subscribe(payload => {
      if (payload === null) {
        this.badDataTicket = Object.assign({}, this.defaultBadDataTicket);
      } else {
        this.badDataTicket = payload;
      }
      this.getBadDataCount();
    });
    this.setUser(JSON.parse(localStorage.getItem('user')) as User);
    this.getMyDetails();
    this.subscription.add(this.userService.userData$.subscribe((u: User) => {
      if (u != null) {
        this.setUser(u);
      }
    }));
  }

  openPasswordChange() {
    window.open('https://account.activedirectory.windowsazure.com/changepassword.aspx', '_blank');
  }

  getPasswordMessage(): string {
    if (this.daysPasswordExpiresIn() <= 0) {
      return 'Your password has expired. Please change it now.';
    } else if (this.daysPasswordExpiresIn() <= 15) {
      return 'Your password expires in ' + this.daysPasswordExpiresIn() + ' days';
    } else {
      return '';
    }
  }

  getMyDetails() {
    this.subscription.add(this.userService.getMyDetails().subscribe({
      next: details => {
        this.myDetails = details;
        if (details?.lastPasswordChangeDateTime) this.doWorkOnUser();
      },
      error: error => this.loggerService.logError(
        `Unable to load password-change details: ${error?.message ?? error}`, 'top-menu.component.ts', 'user', false
      )
    }));
  }

  doWorkOnUser() {
    this.nextPasswordChange = moment(this.myDetails.lastPasswordChangeDateTime).add(180, 'days').toDate();
  }

  daysPasswordExpiresIn(): number {
    return moment(this.nextPasswordChange).diff(moment(), 'days');
  }

  setUser(user: User) {
    const accountChanged = user?.accountName !== this.user?.accountName;
    if (accountChanged) {
      this.powerBIRequest?.unsubscribe();
      this.powerBILoadState = 'idle';
      this.powerBIWorkspaces = [];
      this.powerBIReports = [];
      this.reportsInWorkspace = [];
    }
    this.user = user;
    if (accountChanged) this.downloadImage();
  }

  downloadImage() {
    const accountName = this.user?.accountName ?? null;
    if (accountName === this.photoAccountName &&
      (this.photoLoaded || (this.photoRequest && !this.photoRequest.closed) || this.photoReader)) return;

    this.cancelPhotoRequest();
    if (accountName !== this.photoAccountName) {
      this.photoAccountName = accountName;
      this.photoLoaded = false;
      this.photoUrl = (accountName && localStorage.getItem('userPhotoAccount') === accountName
        ? localStorage.getItem('userPhoto') : null) || './assets/img/noImage.png';
      if (!accountName || localStorage.getItem('userPhotoAccount') !== accountName) {
        localStorage.removeItem('userPhoto');
        localStorage.removeItem('userPhotoAccount');
      }
    }
    if (!accountName) return;

    this.photoRequest = this.userService.downloadImage(accountName.replace('CENTERPOINT-PRO\\', '')).subscribe({
      next: blob => {
        if (!blob?.size) {
          this.loggerService.logError('User photo was empty', 'top-menu.component.ts', 'userPhoto', false);
          return;
        }
        const reader = new FileReader();
        this.photoReader = reader;
        reader.onload = () => {
          if (this.photoReader !== reader) return;
          this.photoReader = undefined;
          if (typeof reader.result !== 'string') {
            this.loggerService.logError('User photo could not be read', 'top-menu.component.ts', 'userPhoto', false);
            return;
          }
          this.photoUrl = reader.result;
          this.photoLoaded = true;
          localStorage.setItem('userPhoto', this.photoUrl);
          localStorage.setItem('userPhotoAccount', accountName);
        };
        reader.onerror = () => {
          if (this.photoReader !== reader) return;
          this.photoReader = undefined;
          this.loggerService.logError('User photo could not be read', 'top-menu.component.ts', 'userPhoto', false);
        };
        reader.readAsDataURL(blob);
      },
      error: error => this.loggerService.logError(
        `User photo unavailable: ${error?.message ?? error}`, 'top-menu.component.ts', 'userPhoto', false
      )
    });
  }

  private cancelPhotoRequest() {
    this.photoRequest?.unsubscribe();
    if (this.photoReader) {
      this.photoReader.onload = null;
      this.photoReader.onerror = null;
      this.photoReader.abort();
      this.photoReader = undefined;
    }
  }

  getPhotoUrl(): string {
    return this.photoUrl;
  }

  getMenu(): void {
    this.userService.userMenu$.subscribe((menu) => {
      if (menu != null) {
        if (menu.subMenu.length > 4) {
          this.recents = menu.subMenu[4].subMenu;
        }
        if (menu.subMenu.length > 3) {
          this.userMenuList = menu.subMenu[3].subMenu;
        }
      }
      setTimeout(() => {
        this.isLoading = false;
      }, 1000);
    });
  }

  userMenuItemClicked(event: Event) {
    const input = event.target as HTMLInputElement;
    const clickMethod = input.value;
    switch (clickMethod) {
      case 'logout':
        this.logout();
        break;
      default:
    }
    console.log('userMenuItemClicked', clickMethod);
  }

  logout() {
    console.log('logout clicked', this.userMenuList);
    this.logoutClicked.emit();
  }

  logClick(sys: string): void {
    this.loggerService.logPageVisit('top menu clicked', sys);
  }

  openYardi(): void {
    window.open('https://www.yardipca.com/91423cppt');
    this.loggerService.logPageVisit('top menu clicked', 'Yardi');
  }

  openYardiOne(): void {
    window.open('https://centerpoint39241.yardione.com');
    this.loggerService.logPageVisit('top menu clicked', 'YardiOne');
  }

  openOffice(): void {
    window.open('https://www.office.com');
    this.loggerService.logPageVisit('top menu clicked', 'MS 365');
  }

  openPowerBI(): void {
    window.open('https://app.powerbi.com/');
    this.loggerService.logPageVisit('top menu clicked', 'Power BI');
  }

  openSharePoint(): void {
    window.open('https://cntcloud.sharepoint.com');
    this.loggerService.logPageVisit('top menu clicked', 'Sharepoint');
  }

  openEgnyte(): void {
    window.open('https://cntcloud.egnyte.com');
    this.loggerService.logPageVisit('top menu clicked', 'Egnyte');
  }

  openSendToEmailWindow() {
    this.loggerService.logPageVisit('top menu clicked', 'Help Mail');
    window.open('mailto:?subject=CUB&body=&to=help@centerpoint.com');
  }

  openHelp() {
    this.loggerService.logPageVisit('top menu clicked', 'Help');
    window.open('https://cntcloud.sharepoint.com/sites/help');
  }

  openMISHelp() {
    this.loggerService.logPageVisit('top menu clicked', 'MIS Help');
    window.open('https://cntcloud.sharepoint.com/sites/Help/SitePages/MIS%20Help.aspx');
  }

  hideSearch(): void {
    this.searchService.closeSearch();
  }

  onKeyUp(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.searchTerm$.next(value);
    this.checkClose(value);
  }

  // Lets ArrowUp/ArrowDown/Enter drive the quick-search popup while the input keeps focus. The keys
  // are forwarded to the popup (via the search service) and their default behavior is suppressed so
  // the caret doesn't jump and the (search) event doesn't re-fire on Enter.
  onSearchKeyDown(event: KeyboardEvent): void {
    let command: 'up' | 'down' | 'enter' | null = null;
    if (event.key === 'ArrowDown') {
      command = 'down';
    } else if (event.key === 'ArrowUp') {
      command = 'up';
    } else if (event.key === 'Enter') {
      command = 'enter';
    }
    if (!command) {
      return;
    }
    event.preventDefault();
    this.searchService.popupKeyCommand(command);
  }

  checkClose(term: string) {
    if (term.length === 0) {
      this.hideSearch();
    }
  }

  openSearch(): void {
    this.searchService.openSearch();
  }

  // Close the quick-search popup when the box loses focus. Delayed so a click on a popup item
  // (a result row or the "Search Files..." button) is processed before *ngIf removes the popup.
  onSearchBlur(): void {
    setTimeout(() => this.searchService.closeSearch(), 200);
  }

  toggleSideMenu(): void {
    this.menuService.toggleSystemMenu(!this.showLeftMenu);
  }

  togglePoint(): void {
    this.menuService.togglePoint(!this.showPoint);
  }

  toggleMinMaxLeftMenu(): void {
    this.menuService.toggleMinMaxLeftMenu(!this.leftMenuIsCollapsed);
  }

  goHome() {
    this.router.navigate(['']);
  }

  configureDefaultBadDataTicket() {
    this.defaultBadDataTicket = new BadDataTicket();
    this.defaultBadDataTicket.assignees.push(new Assignee(Constants.PCKeyKevin, 'Kevin German', 'kgerman@centerpoint.com'));
    this.defaultBadDataTicket.assignees.push(new Assignee(Constants.PCKeyRick, 'Rick Okrasinski', 'rokrasinski@centerpoint.com'));
    this.defaultBadDataTicket.systemKey = Constants.BadDataSystemGeneral;
    this.defaultBadDataTicket.systemDesc = 'General';
    this.defaultBadDataTicket.location = '';
    this.badDataTicket = Object.assign({}, this.defaultBadDataTicket);
  }

  openBadDataTicket() {
    let dialogRef = this.dialog.open(BadDataTicketComponent, {
      disableClose: true,
      width: '675px',
      position: { top: '150px' },
      data: { ticket: this.badDataTicket }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.status === 'saved') {
        this.getBadDataCount();
      }
    });
  }

  openBadDataListing() {
    let dialogRef = this.dialog.open(BadDataComponent, {
      width: '1200px',
      position: { top: '100px' },
      autoFocus: false,
      data: { mode: this.badDataTicket.systemDesc, tableKey: this.getTicketObjectsKey() }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getBadDataCount();  // check if any were resolved?
    });
  }

  getBadDataCount() {
    // get the count based on the system/key
    if (this.badDataTicket.systemDesc === 'General') {
      this.badDataCount = 0;
    } else {
      this.badDataService.getTicketCount(this.badDataTicket.systemDesc, this.getTicketObjectsKey()).subscribe(result => {
        this.badDataCount = result;
      });
    }
  }

  getTicketObjectsKey(): number {
    let tKey = null;
    if (this.badDataTicket.systemDesc === 'Property') {
      tKey = this.badDataTicket.propertyKey;
    } else if (this.badDataTicket.systemDesc === 'Lease') {
      tKey = this.badDataTicket.leaseKey;
    }
    return tKey;
  }

  getUserMenu(): void {
    this.userService.userMenu$.subscribe((menu) => {
      if (menu != null) {
        this.userMenuList = menu.subMenu[3].subMenu;
      }
    });
  }
}
